<script setup lang="ts">
import { ref, computed, onBeforeMount } from 'vue'
import Axios from 'axios'
import QS from 'qs'
import { isNil, replace, forEach } from 'lodash-es'

interface Translation {
  name: string
  content: string
}

interface Translations {
  cs: Translation
}

interface Cover {
  contentUrl: string
}

interface Price {
  priceWithTax: number
}

interface Variant {
  prices: Price[]
  covers: Cover[]
}

interface Product {
  id: number
  count: number
  status: string
  variants: Variant[]
  translations: Translations
}

interface Category {
  products: Product[]
  translations: Translations
}

interface Categories {
  [key: string]: Category
}

interface Order {
  firstName: string | null
  lastName: string | null
  phone: string | null
  emailAddress: string | null
  regNumber: string | null
  vatNumber: string | null
  companyName: string | null
  street: string | null
  city: string | null
  postcode: string | null
  note: string | null
}

const sent = ref(false)
const categories = ref<Categories>({})
const country = ref<'CZ' | 'SK'>('CZ')
const order = ref<Order>({
  firstName: null,
  lastName: null,
  phone: null,
  emailAddress: null,
  regNumber: null,
  vatNumber: null,
  companyName: null,
  street: null,
  city: null,
  postcode: null,
  note: null
})

onBeforeMount(() => {
  const eshopElement = document.querySelector('#eshop')
  if (eshopElement) {
    categories.value = JSON.parse((eshopElement as HTMLElement).dataset.categories || '{}')
    forEach(categories.value, (category: Category, i: string) => {
      forEach(category.products, (product: Product, z: number) => {
        categories.value[i].products[z].count = 0
      })
    })
  }
})

const totalProductPrice = computed(() => {
  const total: number[] = []
  forEach(categories.value, (category: Category) => {
    forEach(category.products, (product: Product) => {
      total.push(parseFloat(totalProduct(product)))
    })
  })
  return total.reduce((a, b) => a + b, 0)
})

const shippingCost = computed(() => (country.value === 'SK' ? 350 : 150))

const totalOrder = computed(() => {
  return totalProductPrice.value + shippingCost.value
})

const cart = computed(() => {
  const cartItems: { id: number; count: number }[] = []
  forEach(categories.value, (category: Category) => {
    forEach(category.products, (product: Product) => {
      if (product.count > 0) {
        cartItems.push({ id: product.id, count: product.count })
      }
    })
  })
  return cartItems
})

const isNilCheck = (path: any) => {
  return isNil(path)
}

const preview = (path: string) => {
  return replace(path, '/media/', '/media/cache/square/')
}

const totalProduct = (product: Product) => {
  if (isNil(product.variants[0]?.prices[0])) {
    return parseFloat('0').toFixed(2)
  }

  if (isNil(product.count) || product.count < 1) {
    return parseFloat('0').toFixed(2)
  }

  return parseFloat(String(product.variants[0].prices[0].priceWithTax / 100 * product.count)).toFixed(2)
}

const submit = () => {
  Axios.post('/eshop', QS.stringify({
    cart: cart.value,
    order: { ...order.value, country: country.value }
  })).then(() => {
    sent.value = true
  })
}
</script>

<template>
  <section class="narrow" v-if="sent">
    <article class="box transparent text-big padding-0">
      <h1>E-shop</h1>
      <p>Objednávka byla odeslána. Platební instrukce vám budou zaslány po zpracování objednávky na uvedený e-mail.</p>
    </article>
  </section>
  <section class="narrow eshop" v-if="!sent">
    <form class="box transparent" @submit.prevent="submit">
      <h1>E-shop</h1>
      <template v-for="category in categories" :key="category.translations.cs.name">
        <div class="category" v-if="category.products.length > 0">
          <header class="items">
            <div>
              <h2>{{ category.translations.cs.name }}</h2>
            </div>
            <div class="item-actions-header">
              <div>Množství<br>[ks]</div>
              <div>Cena za kus<br>[CZK]</div>
              <div>Cena celkem<br>[CZK]</div>
            </div>
          </header>
          <template v-for="product in category.products" :key="product.id">
            <article v-if="product.status == 'published'">
          <div class="item-description">
            <div>
              <a data-fancybox="eshop" :href="product.variants[0].covers[0].contentUrl" v-if="!isNilCheck(product.variants[0].covers[0]?.contentUrl)">
                <img :src="preview(product.variants[0].covers[0].contentUrl)" :alt="product.translations.cs.name">
              </a>
            </div>
            <div>
              <h3>{{ product.translations.cs.name }}</h3>
              <p v-html="product.translations.cs.content"></p>
            </div>
          </div>
          <div class="item-actions">
            <div>
              <span>Množství<br>[ks]</span>
              <input type="number" min="0" value="0" v-model="product.count">
            </div>
            <div>
              <span>Cena za kus<br>[CZK]</span>
              <span v-if="!isNilCheck(product.variants[0].prices[0])">{{ product.variants[0].prices[0].priceWithTax/100 }}</span>
            </div>
            <div>
              <span>Cena celkem<br>[CZK]</span>
              <span>{{ totalProduct(product) }}</span>
            </div>
          </div>
            </article>
          </template>
        </div>
      </template>

      <div>
        Zboží nezasíláme na dobírku, platba pouze předem. Dovolujeme si Vám upozornit, že za poštovné a balné v ČR si účtujeme 150,-. Na Slovensko 350,-.
      </div>

      <div class="order-summary">
        <table>
          <tbody>
            <tr>
              <th>Mezisoučet</th>
              <td>
                <span>{{ totalProductPrice }}</span> CZK
              </td>
            </tr>
            <tr>
              <th>Doprava</th>
              <td>{{ shippingCost }} CZK</td>
            </tr>
            <tr>
              <th>Celkem</th>
              <td>
                <span>{{ totalOrder }}</span> CZK
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="order-info" v-if="parseFloat(String(totalProductPrice)).toFixed(0) > '0'">
        <h2>Fakturace a dodání</h2>
        <div>
          <label class="span-2">
            <span>Země doručení</span>
            <select v-model="country">
              <option value="CZ">Česká republika (poštovné 150 Kč)</option>
              <option value="SK">Slovensko (poštovné 350 Kč)</option>
            </select>
          </label>
        </div>
        <div>
          <label>
            <span>Jméno</span>
            <input type="text" name="first name" required v-model="order.firstName">
          </label>
          <label>
            <span>Příjmení</span>
            <input type="text" name="surname" required v-model="order.lastName">
          </label>
          <label>
            <span>Telefon</span>
            <input type="tel" name="phone" required v-model="order.phone">
          </label>
          <label>
            <span>E-mail</span>
            <input type="email" name="email" required v-model="order.emailAddress">
          </label>
        </div>
        <div>
          <label>
            <span>IČ <small>(volitelně)</small></span>
            <input type="number" v-model="order.regNumber">
          </label>
          <label>
            <span>DIČ <small>(volitelně)</small></span>
            <input type="text" v-model="order.vatNumber">
          </label>
          <label>
            <span>Název firmy <small>(volitelně)</small></span>
            <input type="text" v-model="order.companyName">
          </label>
        </div>
        <div>
          <label>
            <span>Ulice</span>
            <input type="text" name="street" required v-model="order.street">
          </label>
          <label>
            <span>Město</span>
            <input type="text" name="city" required v-model="order.city">
          </label>
          <label>
            <span>PSČ</span>
            <input type="number" name="zip" required v-model="order.postcode">
          </label>
        </div>
        <div>
          <label class="span-2">
            <span>Poznámka <small>(volitelně)</small></span>
            <textarea v-model="order.note"></textarea>
            <small>Např. barvy, velikosti, dodací adresa jiná než fakturační apod.</small>
          </label>
        </div>
        <div>
          <p class="span-2">Odesláním objednávky souhlasíte s <a href="/obchodni-podminky.pdf" target="_blank">obchodními podmínkami</a>.</p>
        </div>
        <button>Odeslat</button>
      </div>
    </form>
  </section>
</template>
